import { useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '@/api/queryKeys'
import type { Unit } from '@/api/pending'
import { uploadFile } from '@/api/upload'
import {
  addToQueue,
  groupByUnit,
  itemsLabel,
  queueTotal,
  type Batch,
  type QueueLine,
} from '@/lib/desk'
import type { StorageLocation } from '@/api/pending'
import { Button, Card, CountStepper, Icon, IconButton, Modal, Row, useToast } from '@/ui'
import { registerItems, unitsWithResidentsQuery } from './api'
import styles from './desk.module.css'
import { CarrierChips, PhotoPicker, StorageFields, type PickedPhoto } from './RegisterParts'
import { deskStrings } from './strings'
import { UnitPad } from './UnitPad'

interface Props {
  condoId: number
  /** "Adicionar item" on a batch: the unit is fixed and the new items join its pickup. */
  prefill?: Batch
  onClose: () => void
}

export function RegisterDeliveriesModal({ condoId, prefill, onClose }: Props) {
  const qc = useQueryClient()
  const toast = useToast()
  const units = useQuery(unitsWithResidentsQuery(condoId))
  const prefillUnit = prefill
    ? units.data?.find((u) => u.number === prefill.unit && u.tower === prefill.tower)
    : undefined

  const [picked, setPicked] = useState<Unit | null>(null)
  const unit = prefill ? (prefillUnit ?? null) : picked
  const [carrier, setCarrier] = useState('Amazon')
  const [count, setCount] = useState(1)
  const [queue, setQueue] = useState<QueueLine[]>([])
  const [location, setLocation] = useState<StorageLocation>('front_desk')
  const [slot, setSlot] = useState('')
  const [photos, setPhotos] = useState<PickedPhoto[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  // Photos already uploaded stay uploaded when a retry follows a partial failure.
  const uploaded = useRef(new Map<File, string>())
  const t = deskStrings.register

  const lines = unit ? addToQueue(queue, { unit, carrier, count }) : queue
  const total = queueTotal(lines)
  const valid = total > 0 && (location !== 'locker' || slot !== '')

  function addLine() {
    if (!unit) return
    setQueue(addToQueue(queue, { unit, carrier, count }))
    setCount(1)
    setPicked(null)
  }

  async function submit() {
    setBusy(true)
    setError('')
    try {
      const ids: string[] = []
      for (const { file } of photos) {
        let id = uploaded.current.get(file)
        if (id === undefined) {
          try {
            id = (
              await uploadFile({
                file,
                resourceType: 'receiving_item_image',
                condominiumId: condoId,
              })
            ).upload_id
          } catch {
            setError(t.uploadFailed)
            return
          }
          uploaded.current.set(file, id)
        }
        ids.push(id)
      }

      // One POST per unit; units that fail stay in the queue for a retry.
      const regs = groupByUnit(lines, {
        storage_location: location,
        locker_slot: slot,
        photo_upload_ids: ids,
      })
      const failed = new Set<number>()
      let registered = 0
      for (const reg of regs) {
        try {
          await registerItems(condoId, {
            unit_id: reg.unit.id,
            items: reg.items.map((i) => ({
              item_type: i.item_type,
              carrier: i.carrier,
              storage_location: location,
              ...(location === 'locker' && slot ? { locker_slot: slot } : {}),
              ...(ids.length ? { photo_upload_ids: ids } : {}),
            })),
          })
          registered += reg.items.length
        } catch {
          failed.add(reg.unit.id)
        }
      }
      if (registered) await qc.invalidateQueries({ queryKey: queryKeys.desk.all(condoId) })
      if (failed.size) {
        setQueue(lines.filter((l) => failed.has(l.unit.id)))
        setPicked(null)
        setCount(1)
        setError(t.submitFailed)
        if (registered)
          toast(`${registered} ${registered > 1 ? 'itens registrados' : 'item registrado'}`)
        return
      }
      toast(`${registered} ${registered > 1 ? 'itens registrados' : 'item registrado'}`)
      onClose()
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      width={620}
      title={prefill ? t.addToPickup(prefill.unit) : t.title}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button icon="check" disabled={!valid} loading={busy} onClick={() => void submit()}>
            {total ? `Registrar ${itemsLabel(total)} e avisar` : 'Registrar e avisar'}
          </Button>
        </>
      }
    >
      {prefill ? (
        <div className={styles.prefill}>
          <Icon name="inventory_2" size={20} />
          <div>
            <div className={styles.prefillTitle}>
              {prefill.count} {prefill.count > 1 ? 'itens já aguardando' : 'item já aguardando'}{' '}
              para {prefill.unit} · {prefill.resident}
            </div>
            <div className={styles.prefillBody}>{t.prefillHint}</div>
          </div>
        </div>
      ) : (
        <UnitPad units={units.data} value={unit?.id} onPick={setPicked} />
      )}
      {unit && !unit.residents.length && <div className={styles.warning}>{t.noResident}</div>}

      <CarrierChips value={carrier} onPick={setCarrier} />
      <CountStepper value={count} onChange={setCount} />
      <StorageFields location={location} slot={slot} onLocation={setLocation} onSlot={setSlot} />
      <PhotoPicker photos={photos} onChange={setPhotos} />

      {!prefill && (
        <div className={styles.addLine}>
          <Button variant="secondary" icon="add" disabled={!unit} onClick={addLine}>
            {t.addUnit}
          </Button>
          <span className={styles.muted}>{t.queueHint}</span>
        </div>
      )}
      {queue.length > 0 && (
        <Card pad={0}>
          {queue.map((l) => (
            <Row key={l.id} className={styles.queueRow}>
              <div className={styles.queueUnit}>
                {l.unit.number} · {l.unit.tower}
              </div>
              <div className={styles.queueCarrier}>{l.carrier}</div>
              <div className={styles.queueCount}>×{l.count}</div>
              <IconButton
                icon="close"
                aria-label={`Remover ${l.carrier} da unidade ${l.unit.number}`}
                onClick={() => setQueue(queue.filter((x) => x.id !== l.id))}
              />
            </Row>
          ))}
        </Card>
      )}
      {error && (
        <div className={styles.error} role="alert">
          <Icon name="error" size={16} />
          {error}
        </div>
      )}
    </Modal>
  )
}
